import * as icons from '@mui/icons-material';
import { Box, Stack, Button, Autocomplete, TextField, Tooltip } from '@mui/material';
import * as refine from '@refinedev/core';
import { Create, Edit, useAutocomplete } from '@refinedev/mui';
import { useEffect, useMemo } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { BaseField } from '../../../components/fields/BaseField.tsx';
import { DateTimeField } from '../../../components/fields/DateTimeField.tsx';
import { ReadOnlyField } from '../../../components/fields/ReadOnlyField.tsx';
import { SelectField } from '../../../components/fields/SelectField.tsx';
import { Organization, Account } from '../../../types/account.ts';
import type { OrganizationWithAccountsAndMagazines } from '../../../types/account.ts';
import type { OrganizationType, Seller } from '../../../types/account.ts';

type OrganizationFormData = OrganizationWithAccountsAndMagazines & {
  adminAccountName?: string;
  adminAccountEmail?: string;
};

const COMPONENT_MAP = { create: Create, edit: Edit } as const;

export const OrganizationForm = ({ action }: { action: 'create' | 'edit' }) => {
  const navigate = useNavigate();
  const { query, formLoading, onFinish } = refine.useForm<OrganizationFormData>({
    resource: 'accounts/organizations',
    action,
  });

  const apiUrl = refine.useApiUrl();
  const { mutate: resendMutate, isLoading: isResending } = refine.useCustomMutation();

  const data = useMemo(() => {
    if (!query?.data?.data) return undefined;
    const rawData = query.data.data as OrganizationFormData;
    return {
      ...Organization.parse(rawData),
      accounts: rawData.accounts,
      magazines: rawData.magazines,
      adminAccountName: rawData.adminAccountName,
      adminAccountEmail: rawData.adminAccountEmail,
    };
  }, [query?.data?.data]);

  const methods = useForm<OrganizationFormData>({
    values: data,
    defaultValues: {
      status: 'active',
      accountLimitNum: 1,
      organizationType: {
        id: '',
        name: '',
      },
      magazines: {
        nbl: { accountLimitNum: 0 },
        shiryoban: { accountLimitNum: 0 },
        junkan: { accountLimitNum: 0 },
      },
    },
  });

  const { autocompleteProps: organizationTypeAutocompleteProps } =
    useAutocomplete<OrganizationType>({
      resource: 'accounts/organization-types',
      filters: [],
    });

  const { autocompleteProps: sellerAutocompleteProps } = useAutocomplete<Seller>({
    resource: 'accounts/sellers',
    filters: [],
  });

  useEffect(() => {
    if (action === 'create') {
      // APIからoptionを取得した後に指定したデフォルト値があれば設定する
      if (organizationTypeAutocompleteProps.options.length > 0) {
        const defaultOrgType = organizationTypeAutocompleteProps.options.find(
          (opt) => opt.id === 'organization-type-1',
        );
        if (defaultOrgType && methods.getValues('organizationType')?.id === '') {
          methods.setValue('organizationType', defaultOrgType);
        }
      }
      if (sellerAutocompleteProps.options.length > 0) {
        const defaultSeller = sellerAutocompleteProps.options.find((opt) => opt.id === 'seller-1');
        if (defaultSeller && !methods.getValues('seller')) {
          methods.setValue('seller', defaultSeller);
        }
      }
    }
  }, [action, organizationTypeAutocompleteProps.options, sellerAutocompleteProps.options, methods]);

  const { mutate } = refine.useUpdate({
    resource: `accounts/organizations`,
  });

  const status = methods.watch('status');

  const handleResendAdminEmails = (accountId: string, accountEmail: string) => {
    if (!data?.id) {
      return;
    }

    if (
      !window.confirm(`${accountEmail} に招待メールと組織作成メールを再送してもよろしいですか？`)
    ) {
      return;
    }

    resendMutate({
      url: `${apiUrl}/accounts/organizations/${data.id}/accounts/${accountId}/resend-admin-emails`,
      method: 'post',
      values: {},
      successNotification: { message: 'メールを再送しました', type: 'success' },
    });
  };

  const handleResendOrganizationCreatedEmail = (accountId: string, accountEmail: string) => {
    if (!data?.id) {
      return;
    }

    if (!window.confirm(`${accountEmail} に組織作成メールを再送してもよろしいですか？`)) {
      return;
    }

    resendMutate({
      url: `${apiUrl}/accounts/organizations/${data.id}/accounts/${accountId}/resend-organization-created-email`,
      method: 'post',
      values: {},
      successNotification: { message: 'メールを再送しました', type: 'success' },
    });
  };

  const toggleStatus = () => {
    if (!data?.id) return;
    if (
      !window.confirm(
        `この組織の利用を${status === 'active' ? '停止' : '再開'}してもよろしいですか？`,
      )
    )
      return;

    mutate(
      {
        id: data.id,
        values: { ...data, status: status === 'active' ? 'inactive' : 'active' },
      },
      {
        onSuccess: () => {
          navigate(`/accounts/organizations`);
        },
      },
    );
  };

  const Component = COMPONENT_MAP[action];

  return (
    <Component
      isLoading={formLoading}
      saveButtonProps={{
        disabled: methods.formState.isSubmitting || formLoading,
        onClick: methods.handleSubmit(onFinish),
      }}
      footerButtons={({ defaultButtons }: { defaultButtons: React.ReactNode }) => (
        <>
          <Box display="flex" flexDirection="column" gap={4}>
            {defaultButtons}
            {action === 'edit' && data?.id && (
              <Button
                variant="contained"
                color="error"
                onClick={toggleStatus}
                sx={{ minWidth: 200 }}
              >
                この組織の利用を{status === 'active' ? '停止' : '再開'}する
              </Button>
            )}
          </Box>
        </>
      )}
    >
      <FormProvider {...methods}>
        <Stack spacing={2}>
          <Box display="flex" gap={2}>
            <Controller
              name="validFrom"
              control={methods.control}
              rules={{ required: '必須' }}
              render={({ field, fieldState }) => (
                <DateTimeField
                  {...field}
                  type="date"
                  label="利用開始日"
                  fullWidth
                  sx={{ flex: 1 }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="organizationType"
              control={methods.control}
              rules={{ required: '必須' }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...organizationTypeAutocompleteProps}
                  {...field}
                  onChange={(_, value) => {
                    field.onChange(value);

                    if (value.id === 'organization-type-2') {
                      const accountLimitNum = methods.getValues('accountLimitNum');
                      methods.setValue('magazines.junkan.accountLimitNum', accountLimitNum);
                    }
                  }}
                  getOptionKey={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  disableClearable
                  sx={{ flex: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="組織タイプ"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            />
            <SelectField
              {...sellerAutocompleteProps}
              name="seller"
              rules={{
                required: '必須',
              }}
              label="販売経路"
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
              disableClearable
              sx={{ flex: 1 }}
            />
          </Box>
          <BaseField
            name="name"
            type="text"
            rules={{
              required: '必須',
            }}
            label="組織名"
            fullWidth
          />
          <Box display="flex" gap={2}>
            <BaseField
              name="accountLimitNum"
              type="number"
              rules={{
                required: '必須',
                min: {
                  value: 0,
                  message: '0以上の数値を入力してください',
                },
              }}
              label="利用可能ユーザー数"
              sx={{ flex: 1 }}
            />
            <BaseField
              name="magazines.nbl.accountLimitNum"
              type="number"
              rules={{
                required: '必須',
                min: {
                  value: 0,
                  message: '0以上の数値を入力してください',
                },
              }}
              label="NBLユーザー数"
              sx={{ flex: 1 }}
            />
            <BaseField
              name="magazines.shiryoban.accountLimitNum"
              type="number"
              rules={{
                required: '必須',
                min: {
                  value: 0,
                  message: '0以上の数値を入力してください',
                },
              }}
              label="資料版商事法務ユーザー数"
              sx={{ flex: 1 }}
            />
            <BaseField
              name="magazines.junkan.accountLimitNum"
              type="number"
              rules={{
                required: '必須',
                min: {
                  value: 0,
                  message: '0以上の数値を入力してください',
                },
              }}
              label="旬刊商事法務ユーザー数"
              sx={{ flex: 1 }}
            />
          </Box>
          {action === 'create' ? (
            <Box display="flex" gap={2}>
              <BaseField
                name="adminAccountName"
                type="text"
                rules={{
                  required: '必須',
                }}
                label="組織管理者氏名"
                sx={{ flex: 1 }}
              />
              <BaseField
                name="adminAccountEmail"
                type="text"
                rules={{
                  required: '必須',
                  validate: (value) => {
                    const result = Account.shape.email.safeParse(value);
                    if (result.success) {
                      return true;
                    } else {
                      return result.error.issues.at(0)?.message;
                    }
                  },
                }}
                label="組織管理者メールアドレス"
                sx={{ flex: 1 }}
              />
            </Box>
          ) : (
            data?.accounts &&
            data.accounts.map((account, index) => (
              <Box key={index} display="flex" gap={2} alignItems="flex-end">
                <ReadOnlyField
                  name="adminAccountName"
                  type="text"
                  label="組織管理者氏名"
                  sx={{ flex: 1 }}
                >
                  {account.name}
                </ReadOnlyField>
                <ReadOnlyField
                  name="adminAccountEmail"
                  type="text"
                  label="組織管理者メールアドレス"
                  sx={{ flex: 1 }}
                >
                  {account.email}
                </ReadOnlyField>
                <Tooltip title="組織管理者へのアカウント招待メールと組織作成メールを再送します">
                  <Button
                    variant="outlined"
                    startIcon={<icons.EmailOutlined fontSize="small" />}
                    onClick={() => handleResendAdminEmails(account.id, account.email)}
                    disabled={isResending}
                    sx={{ minWidth: 120, height: 56 }}
                  >
                    {isResending ? '送信中...' : '招待メール再送'}
                  </Button>
                </Tooltip>
                <Tooltip title="組織管理者への組織作成メールのみを再送します">
                  <Button
                    variant="outlined"
                    startIcon={<icons.EmailOutlined fontSize="small" />}
                    onClick={() => handleResendOrganizationCreatedEmail(account.id, account.email)}
                    disabled={isResending}
                    sx={{ minWidth: 120, height: 56 }}
                  >
                    {isResending ? '送信中...' : '組織メール再送'}
                  </Button>
                </Tooltip>
              </Box>
            ))
          )}
        </Stack>
      </FormProvider>
    </Component>
  );
};
